'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import useSWR from 'swr'

import { FooterV3 } from '@/components/home-v3/footer'
import { HeaderV3 } from '@/components/home-v3/header'
import { TemplateCard } from '@/components/template/template-card'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { CATEGORIES } from '@/lib/constant'
import type { Template } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'

const PAGE_SIZE = 12 // We can show more items per page on the dedicated template page

const TEMPLATE_CATEGORIES = [
  { id: 'all', label: 'All' },
  ...CATEGORIES,
] as const

const TemplateListSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
    {Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="rounded-xl bg-gray-900/50 animate-pulse">
        <div className="aspect-[4/3] w-full rounded-lg bg-gray-800/60" />
        <div className="p-4">
          <div className="h-4 w-3/4 rounded bg-gray-700/80" />
          <div className="mt-2 h-3 w-1/2 rounded bg-gray-700/60" />
        </div>
      </div>
    ))}
  </div>
)

const TemplateCategories = ({
  activeCategory,
  setActiveCategory,
}: {
  activeCategory: string
  setActiveCategory: (category: string) => void
}) => {
  const t = useTranslations('templates.categories')
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth * 0.8
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div className="flex items-center gap-2 mb-12">
      <button
        type="button"
        onClick={() => scroll('left')}
        className="p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto flex-1 no-scrollbar"
      >
        {TEMPLATE_CATEGORIES.map((category) => (
          <button
            type="button"
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 border',
              activeCategory === category.id
                ? 'bg-white text-black border-white'
                : 'bg-transparent text-gray-300 border-gray-700 hover:bg-gray-800 hover:border-gray-600',
            )}
          >
            {t(category.id)}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => scroll('right')}
        className="p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}

const TemplateListData = ({
  category,
  page,
  setPage,
}: {
  category: string
  page: number
  setPage: (page: number) => void
}) => {
  const skip = (page - 1) * PAGE_SIZE
  const categoryParam =
    category === 'all' ? '' : `&category=${encodeURIComponent(category)}`
  const url = `/api/templates?skip=${skip}&limit=${PAGE_SIZE}${categoryParam}`

  const { data, error, isLoading } = useSWR<{
    data: Template[]
    total: number
  }>(url, fetcher)

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">
        Failed to load templates. Please try again later.
      </div>
    )
  }
  if (isLoading || !data) return <TemplateListSkeleton />

  const templates = data.data || []
  const totalPages = data.total ? Math.ceil(data.total / PAGE_SIZE) : 0

  return (
    <>
      {templates.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {templates.map((template) => (
            <TemplateCard key={template._id.toString()} template={template} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <p className="text-gray-400">No templates found in this category.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center py-12 mt-8">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setPage(Math.max(1, page - 1))
                  }}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
              <PaginationItem>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setPage(Math.min(totalPages, page + 1))
                  }}
                  className={
                    page >= totalPages ? 'pointer-events-none opacity-50' : ''
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}

export default function TemplateIndexPageClient() {
  const [activeCategory, setActiveCategory] = useState('all')
  const [page, setPage] = useState(1)
  const t = useTranslations('template-index')

  // Reset page to 1 when category changes
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category)
    setPage(1)
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <HeaderV3 />
      <main className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight">
              {t('h1')}
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
              {t('subtitle')}
            </p>
          </div>
          <TemplateCategories
            activeCategory={activeCategory}
            setActiveCategory={handleCategoryChange}
          />
          <TemplateListData
            category={activeCategory}
            page={page}
            setPage={setPage}
          />
        </div>
      </main>
      <FooterV3 />
    </div>
  )
}
